import React, { useEffect, useState } from "react";
import {
  useNavigate,
  useParams,
  useLocation,
  useOutletContext,
} from "react-router-dom";
import { getTopicsBySubjectKey } from "../../../helper/apicalls/course";
import { getTopicsInstitution } from "../../../helper/apicalls/institution/institutionCourse";
import {
  createMaterialSubjectFolder,
  getMaterialFolders,
  updateMaterialFolder,
  deleteMaterialFolder,
} from "../../../helper/apicalls/materials";
import {
  getAdminMaterialFolderInInstitution,
  getMaterialFoldersInstitution,
  createMaterialFolderInstitution,
  updateMaterialFolderInstitution,
  deleteMaterialFolderInstitution,
} from "../../../helper/apicalls/institution/materials";
import CreateFolderPopup from "../../common/CreateFolderPopup";
import DeleteConfirmPopUp from "../../common/deleteConfirmPopUp";
import { HomeOutlinedIcon, AddIcon, FolderIcon } from "../../../icons";
import GlobalLoader from "../../common/GlobalLoader";
import "./../../../styles/admin/questionBank.scss";
import Cookies from "js-cookie";

const MaterialsTopics = () => {
  const { courseKey, subjectKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { setHeaderName } = useOutletContext();
  const [topics, setTopics] = useState([]);
  const [courseName, setCourseName] = useState("");
  const [subjectName, setSubjectName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [folders, setFolders] = useState([]);
  const [showCreateFolderPopup, setShowCreateFolderPopup] = useState(false);
  const [folderMenuOpenId, setFolderMenuOpenId] = useState(null);
  const [editFolder, setEditFolder] = useState(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const role = Cookies.get("role");
  const permissions = Cookies.get("permissions");
  const permissionsData = permissions ? JSON.parse(permissions) : [];
  const materialPermission = Array.isArray(permissionsData)
    ? permissionsData.find((p) => p?.permission_type === "material")
    : null;
  const hasWritePermission =
    role === "admin" || (materialPermission ? !!materialPermission.write : false);

  useEffect(() => {
    fetchData();
    setHeaderName("Materials - Topics");
  }, [courseKey, subjectKey, location]);

  useEffect(() => {
    if (!folderMenuOpenId) return;

    const handleClickOutside = (event) => {
      const menus = document.querySelectorAll(
        ".folder-menu, .folder-menu-button"
      );
      for (let i = 0; i < menus.length; i += 1) {
        const el = menus[i];
        if (el && el.contains(event.target)) {
          return;
        }
      }

      setFolderMenuOpenId(null);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [folderMenuOpenId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const role = Cookies.get("role");
      let topicsData,
        adminFoldersData = [],
        institutionFoldersData = [],
        foldersData = [];

      if (role === "institution" || role === "teacher") {
        topicsData = await getTopicsInstitution(subjectKey);
        adminFoldersData = await getAdminMaterialFolderInInstitution(
          "subject",
          subjectKey
        );
        institutionFoldersData = await getMaterialFoldersInstitution(
          "subject",
          subjectKey
        );

        foldersData = [
          ...adminFoldersData.map((folder) => ({ ...folder, is_admin: true })),
          ...institutionFoldersData.map((folder) => ({
            ...folder,
            is_admin: false,
          })),
        ];
      } else {
        topicsData = await getTopicsBySubjectKey(subjectKey);
        foldersData = await getMaterialFolders("subject", subjectKey);
      }

      if (Array.isArray(topicsData) && topicsData.length > 0) {
        setTopics(topicsData);
        if (topicsData[0].subject) {
          setSubjectName(topicsData[0].subject.name || "");
          if (topicsData[0].subject.course) {
            setCourseName(topicsData[0].subject.course.name || "");
          }
        }
      } else {
        setTopics([]);
      }

      setFolders(Array.isArray(foldersData) ? foldersData : []);
    } catch (err) {
      console.error("Error fetching topics for Materials:", err);
      setError("Failed to fetch topics");
      setTopics([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTopicClick = (topic) => {
    const role = Cookies.get("role");
    const portalBase = role === "admin" ? "/admin" : role === "teacher" ? "/teacher" : "/institution";
    navigate(`${portalBase}/materials/course/${courseKey}/subject/${subjectKey}/topic/${topic.key}`);
  };

  const handleFolderClick = (folder) => {
    if (!folder?.id) return;
    const role = Cookies.get("role");
    const portalBase = role === "admin" ? "/admin" : role === "teacher" ? "/teacher" : "/institution";
    navigate(`${portalBase}/materials/folder/${folder.id}`, {
      state: {
        level: "subject",
        courseKey: courseKey,
        subjectKey: subjectKey,
        folderName: folder.name,
        is_admin: folder.is_admin || false,
      },
    });
  };

  const handleGoUpOneLevel = () => {
    const role = Cookies.get("role");
    const portalBase = role === "admin" ? "/admin" : role === "teacher" ? "/teacher" : "/institution";
    navigate(`${portalBase}/materials/course/${courseKey}`);
  };

  const handleAddFolder = () => {
    setShowCreateFolderPopup(true);
  };

  const handleFolderMenuToggle = (id, e) => {
    e.stopPropagation();
    setFolderMenuOpenId((prev) => (prev === id ? null : id));
  };

  const handleFolderEdit = (folder, e) => {
    e.stopPropagation();
    setEditFolder(folder);
  };

  const handleFolderDelete = (folder, e) => {
    e.stopPropagation();
    setFolderToDelete(folder);
    setIsConfirmDeleteOpen(true);
  };

  const breadcrumbLinks = [
    { icon: <HomeOutlinedIcon />, path: "/admin/materials" },
    {
      label: courseName || "Course " + courseKey,
      path: `/admin/materials/course/${courseKey}`,
    },
    { label: subjectName || "Subject " + subjectKey, active: true },
  ];

  return (
    <>
      <GlobalLoader visible={loading} text="Loading..." />
      <div className="question-bank-container">
        <div className="qb-card">
          <div className="qb-card-header">
            <span>Subject Materials</span>
            <div className="qb-card-actions">
              {hasWritePermission && (
                <button
                  type="button"
                  className="qb-add-btn"
                  onClick={handleAddFolder}
                >
                  <AddIcon className="action-button-icon" />
                  <span>Add Folder</span>
                </button>
              )}
            </div>
          </div>

          <div className="folder-grid">
            {!loading &&
              !error &&
              topics?.map((top) => {
                const id = top.id || top.key;
                return (
                  <div
                    key={id}
                    className="folder-item"
                    onClick={() => handleTopicClick(top)}
                  >
                    <div className="folder-icon">
                      <FolderIcon style={{ color: "#fccc77" }} />
                    </div>
                    <div className="folder-name">{top.name}</div>
                  </div>
                );
              })}

            {!loading &&
              !error &&
              folders?.map((folder) => {
                const id = folder.id || folder.key;
                return (
                  <div
                    key={id}
                    className="folder-item"
                    onClick={() => handleFolderClick(folder)}
                  >
                    <div className="folder-icon">
                      <FolderIcon style={{ color: "var(--folder-bg)" }} />
                    </div>
                    <div className="folder-name">{folder.name}</div>
                    {(role === "institution" || role === "teacher") && folder.is_admin && (
                      <div className="qb-default-badge">Default</div>
                    )}
                    {!folder.is_admin && hasWritePermission && (
                      <button
                        type="button"
                        className="folder-menu-button"
                        onClick={(e) => handleFolderMenuToggle(id, e)}
                      >
                        ⋮
                      </button>
                    )}
                    {!folder.is_admin && hasWritePermission && folderMenuOpenId === id && (
                      <div
                        className="folder-menu"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className="folder-menu-item"
                          onClick={(e) => handleFolderEdit(folder, e)}
                        >
                          Edit
                        </div>
                        <div
                          className="folder-menu-item --danger"
                          onClick={(e) => handleFolderDelete(folder, e)}
                        >
                          Delete
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {showCreateFolderPopup && (
        <CreateFolderPopup
          open={showCreateFolderPopup}
          handleClose={() => setShowCreateFolderPopup(false)}
          title="subject"
          contextKey={subjectKey}
          mode="create"
          onSave={async (payload) => {
            try {
              setLoading(true);
              const role = Cookies.get("role");
              if (role === "institution" || role === "teacher") {
                await createMaterialFolderInstitution({
                  title: payload.title,
                  key: payload.key,
                  name: payload.name,
                });
              } else {
                await createMaterialSubjectFolder(
                  payload.title,
                  payload.key,
                  payload.name
                );
              }

              const roleRefresh = Cookies.get("role");
              let adminFoldersData = [],
                institutionFoldersData = [],
                foldersData = [];
              if (roleRefresh === "institution" || roleRefresh === "teacher") {
                adminFoldersData = await getAdminMaterialFolderInInstitution(
                  "subject",
                  subjectKey
                );
                institutionFoldersData = await getMaterialFoldersInstitution(
                  "subject",
                  subjectKey
                );

                foldersData = [
                  ...adminFoldersData.map((folder) => ({
                    ...folder,
                    is_admin: true,
                  })),
                  ...institutionFoldersData.map((folder) => ({
                    ...folder,
                    is_admin: false,
                  })),
                ];
              } else {
                foldersData = await getMaterialFolders("subject", subjectKey);
              }
              setFolders(Array.isArray(foldersData) ? foldersData : []);
            } catch (err) {
              console.error("Error creating subject materials folder:", err);
              setError("Failed to create folder");
            } finally {
              setLoading(false);
            }
          }}
        />
      )}

      {!!editFolder && (
        <CreateFolderPopup
          open={!!editFolder}
          handleClose={() => setEditFolder(null)}
          title="subject"
          contextKey={subjectKey}
          mode="edit"
          initialName={editFolder?.name || ""}
          folderId={editFolder?.id}
          onSave={async (payload) => {
            try {
              if (editFolder?.is_admin) {
                console.warn("Cannot edit admin folder");
                return;
              }

              if (!payload.id) return;
              setLoading(true);
              const role = Cookies.get("role");
              if (role === "institution" || role === "teacher") {
                await updateMaterialFolderInstitution(payload.id, payload.name);
              } else {
                await updateMaterialFolder(payload.id, payload.name);
              }

              const roleRefresh = Cookies.get("role");
              let adminFoldersData = [],
                institutionFoldersData = [],
                foldersData = [];
              if (roleRefresh === "institution" || roleRefresh === "teacher") {
                adminFoldersData = await getAdminMaterialFolderInInstitution(
                  "subject",
                  subjectKey
                );
                institutionFoldersData = await getMaterialFoldersInstitution(
                  "subject",
                  subjectKey
                );

                foldersData = [
                  ...adminFoldersData.map((folder) => ({
                    ...folder,
                    is_admin: true,
                  })),
                  ...institutionFoldersData.map((folder) => ({
                    ...folder,
                    is_admin: false,
                  })),
                ];
              } else {
                foldersData = await getMaterialFolders("subject", subjectKey);
              }
              setFolders(Array.isArray(foldersData) ? foldersData : []);
            } catch (err) {
              console.error("Error updating subject materials folder:", err);
              setError("Failed to update folder");
            } finally {
              setLoading(false);
              setEditFolder(null);
            }
          }}
        />
      )}

      {isConfirmDeleteOpen && (
        <DeleteConfirmPopUp
          isConfirmDeleteOpen={isConfirmDeleteOpen}
          setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
          title="Delete folder"
          message={`Are you sure you want to delete the folder "${
            folderToDelete?.name || ""
          }"? This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          handleDelete={async () => {
            if (folderToDelete?.is_admin) {
              setIsConfirmDeleteOpen(false);
              return;
            }

            if (!folderToDelete?.id) {
              setIsConfirmDeleteOpen(false);
              return;
            }
            try {
              setLoading(true);
              const role = Cookies.get("role");
              if (role === "institution" || role === "teacher") {
                await deleteMaterialFolderInstitution(folderToDelete.id);
              } else {
                await deleteMaterialFolder(folderToDelete.id);
              }

              const roleRefresh = Cookies.get("role");
              let adminFoldersData = [],
                institutionFoldersData = [],
                foldersData = [];
              if (roleRefresh === "institution" || roleRefresh === "teacher") {
                adminFoldersData = await getAdminMaterialFolderInInstitution(
                  "subject",
                  subjectKey
                );
                institutionFoldersData = await getMaterialFoldersInstitution(
                  "subject",
                  subjectKey
                );

                foldersData = [
                  ...adminFoldersData.map((folder) => ({
                    ...folder,
                    is_admin: true,
                  })),
                  ...institutionFoldersData.map((folder) => ({
                    ...folder,
                    is_admin: false,
                  })),
                ];
              } else {
                foldersData = await getMaterialFolders("subject", subjectKey);
              }
              setFolders(Array.isArray(foldersData) ? foldersData : []);
            } catch (err) {
              console.error("Error deleting subject materials folder:", err);
              setError("Failed to delete folder");
            } finally {
              setLoading(false);
              setIsConfirmDeleteOpen(false);
              setFolderToDelete(null);
            }
          }}
        />
      )}
    </>
  );
};

export default MaterialsTopics;
